// The lab-results chat card. WF72 writes the body and scripts/lab-results-card.mjs owns its grammar,
// so the back office parses it with the same code the workflow rendered it with.

import {
  isLabCardRow, parseLabResultsBody, labBar, neutralizeLabRow, LAB_HEADER, LAB_PROVENANCE, LAB_FOOTER, LAB_PENDING_LINE,
  labReleaseMarkerOk, labSameInstant, LAB_HOLD_REASONS, LAB_RELEASE_WINDOW_MS,
  labNoteClean, labNoteOk, labNoteName, labNoteFrozen, labNoteCurrent, LAB_NOTE_LIMITS,
  type LabNoteModel, type LabNoteStored,
  type LabCardModel, type LabMeasuredRow, type LabNoValueRow, type LabRow, type LabSection, type LabBarGeometry,
  type LabReleaseMarker, type LabReleaseState, type LabHoldReason,
} from '../../../scripts/lab-results-card.mjs';

export {
  isLabCardRow, labBar, neutralizeLabRow, LAB_HEADER, LAB_PROVENANCE, LAB_FOOTER, LAB_PENDING_LINE,
  labReleaseMarkerOk, labSameInstant, LAB_HOLD_REASONS, LAB_RELEASE_WINDOW_MS,
  labNoteClean, labNoteOk, labNoteName, labNoteFrozen, labNoteCurrent, LAB_NOTE_LIMITS,
};
export type {
  LabNoteModel, LabNoteStored,
  LabCardModel, LabMeasuredRow, LabNoValueRow, LabRow, LabSection, LabBarGeometry,
  LabReleaseMarker, LabReleaseState, LabHoldReason,
};

// Patient copy the module renders but does not export; LabResultsCard.test pins both against its body.
export const LAB_OUTSIDE_NOTE = 'A result outside the range is not always a problem.';
export function labHiddenCopy(n: number): string {
  return n === 1
    ? '1 more result is not shown here. Your care team will go over it with you.'
    : `${n} more results are not shown here. Your care team will go over them with you.`;
}

// A card only for WF72's own row whose body parses; anything else keeps the plain text.
export function labCardOf(row: unknown): LabCardModel | null {
  return isLabCardRow(row) ? parseLabResultsBody(row.body) : null;
}

export function isNoValueRow(row: LabRow): row is LabNoValueRow {
  return 'novalue' in row;
}

// Names are not unique (calculated and direct LDL both read "LDL cholesterol"), so rows carry an id.
export type LabCardRow = LabMeasuredRow & { id: string };
export type LabCardNoValueRow = LabNoValueRow & { id: string };

export interface LabCardGroup {
  key: string;
  label: string;
  rows: LabCardRow[];
}

export interface LabCardLayout {
  open: LabCardGroup[];
  folded: LabCardGroup[];
  noValue: LabCardNoValueRow[];
  foldedCount: number;
}

const OPEN_KEYS = new Set(['outside', 'plan']);
// The patient reads "YOUR CARE PLAN LABS"; the care team reads the same rows as the plan's labs.
const STAFF_LABEL: Record<string, string> = { plan: 'CARE PLAN LABS' };

const measured = (s: LabSection): LabCardGroup => ({
  key: s.key,
  label: STAFF_LABEL[s.key] ?? s.label,
  rows: s.rows.flatMap((r, i) => (isNoValueRow(r) ? [] : [{ ...r, id: `${s.key}:${i}` }])),
});

// Outside-range and care-plan rows stay open; every body system and the no-value list fold away.
export function labCardLayout(model: LabCardModel): LabCardLayout {
  const open = model.sections.filter((s) => OPEN_KEYS.has(s.key)).map(measured);
  const folded = model.sections.filter((s) => !OPEN_KEYS.has(s.key) && s.key !== 'novalue').map(measured);
  const noValue = model.sections
    .filter((s) => s.key === 'novalue')
    .flatMap((s) => s.rows.flatMap((r, i) => (isNoValueRow(r) ? [{ ...r, id: `${s.key}:${i}` }] : [])));
  return {
    open, folded, noValue, foldedCount: folded.reduce((n, g) => n + g.rows.length, 0),
  };
}
